import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Bell,
  BellRing,
  Bug,
  Building2,
  ClipboardList,
  FileText,
  HelpCircle,
  History,
  AlertTriangle,
  Inbox,
  LayoutDashboard,
  Map as MapIcon,
  MapPin,
  Megaphone,
  MessageSquareText,
  Radar,
  ScrollText,
  Send,
  Activity,
  Settings,
  ShieldCheck,
  Smartphone,
  Tags,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react'
import { listCompanies } from '@/api/companies'
import { listUsers } from '@/api/users'
import { getOpenFeedbackCount } from '@/api/user_feedback'
import { getNewLeadsCount } from '@/api/inspection-leads'
import { signOut } from '@/api/auth'
import { useAuthStore } from '@/store/authStore'
import { useUiStore } from '@/store/uiStore'
import type { Profile } from '@/types/user'
import { Button } from '@/components/ui/Button'
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher'
import { shortDisplayName } from '@/utils/profile-name'
import { formatPhoneDisplay } from '@/utils/phone'
import { isStaff } from '@/utils/access'
import { roleDisplayName } from '@/utils/roleDisplayName'
import { useRoleOrderParams } from '@/hooks/useRoleOrderParams'
import { useOrdersForMap } from '@/hooks/useOrdersForMap'
import { createClient } from '@/api/supabase'
import { USE_MOCK } from '@/config'

function NavLink({
  to,
  label,
  count,
  icon: Icon,
  onNavigate,
  exact,
}: {
  to: string
  label: string
  count?: number
  icon?: LucideIcon
  onNavigate?: () => void
  /** Подсвечивать только при точном совпадении пути (для родителя, у которого есть под-роут). */
  exact?: boolean
}) {
  const loc = useLocation()
  const active = exact
    ? loc.pathname === to
    : loc.pathname === to || (to !== '/' && loc.pathname.startsWith(to))
  return (
    <Link
      to={to}
      onClick={onNavigate}
      className={clsx(
        /* v2 просит 15px для пунктов — в design-tokens нет 15px, используем fontSize.base */
        'flex items-center gap-2 rounded-[var(--stryzh-radius-md)] px-3 py-2 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] leading-snug transition',
        active
          ? 'bg-[color:var(--stryzh-color-status-badge-accent-bg)] text-[color:var(--stryzh-color-accent-orange)]'
          : 'text-[color:var(--stryzh-color-text-secondary)] hover:bg-[color:var(--stryzh-color-border-subtle)] hover:text-[color:var(--stryzh-color-text-primary)]',
      )}
    >
      {Icon ? <Icon size={16} className="shrink-0" aria-hidden /> : null}
      <span className="flex-1 truncate">{label}</span>
      {count != null ? (
        <span className="inline-flex min-h-[1.25rem] min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-[color:var(--stryzh-color-accent-orange)] px-1.5 font-[family-name:var(--stryzh-font-family-mono)] text-[length:var(--stryzh-font-size-xs)] font-bold text-[color:var(--stryzh-color-text-on-orange-badge)]">
          {count}
        </span>
      ) : null}
    </Link>
  )
}

const NOTIFICATIONS_PATHS = [
  '/notifications/log',
  '/notifications/triggers',
  '/notifications/broadcast',
  '/notifications/broadcasts',
  '/notifications/subscriptions',
  '/notifications/app-versions',
]

const SYSTEM_SETTINGS_PATHS = ['/system-config', '/geo-settings', '/telegram-settings', '/sms-settings', '/sber-sms-settings']

const LOGS_PATHS = ['/avito-api-log', '/sber-notify-log', '/audit', '/client-errors', '/pd-log']

function NavGroup({
  label,
  children,
  paths,
  icon: Icon,
}: {
  label: string
  children: React.ReactNode
  paths: string[]
  icon?: LucideIcon
}) {
  const loc = useLocation()
  const anyActive = paths.some((p) => loc.pathname.startsWith(p))
  const [open, setOpen] = useState(() => anyActive)

  // Auto-open when navigating into the group from outside
  if (anyActive && !open) setOpen(true)

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={clsx(
          'flex w-full items-center gap-2 rounded-[var(--stryzh-radius-md)] px-3 py-2 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] leading-snug transition',
          anyActive
            ? 'text-[color:var(--stryzh-color-accent-orange)]'
            : 'text-[color:var(--stryzh-color-text-secondary)] hover:bg-[color:var(--stryzh-color-border-subtle)] hover:text-[color:var(--stryzh-color-text-primary)]',
        )}
      >
        {Icon ? <Icon size={16} className="shrink-0" aria-hidden /> : null}
        <span className="flex-1 truncate text-left">{label}</span>
        <span className={clsx('text-xs transition-transform', open ? 'rotate-90' : '')} aria-hidden>
          ▶
        </span>
      </button>
      {open && (
        <div className="ml-3 mt-0.5 flex flex-col gap-0.5 border-l border-[color:var(--stryzh-color-border-subtle)] pl-2">
          {children}
        </div>
      )}
    </div>
  )
}

const SIDEBAR_ACTIVE_STATUSES = [
  'DRAFT',
  'SEARCH',
  'ASSIGNED',
  'DRIVING',
  'ARRIVED',
  'INSPECTION',
  'APPRAISAL',
  'BUYING',
  'DOCUMENT_PREPARATION',
  'PAYMENT_TRANSFER',
  'TRANSPORTATION',
] as const

function useActiveOrderCount() {
  const roleParams = useRoleOrderParams()
  const { data: activeCount = 0 } = useQuery({
    queryKey: ['orders', 'active-count-v2', roleParams],
    queryFn: async () => {
      if (USE_MOCK) {
        // mock: count locally from mockState (no network)
        const { mockState } = await import('@/api/mock-data')
        return mockState.orders.filter((o) =>
          (SIDEBAR_ACTIVE_STATUSES as readonly string[]).includes(o.status),
        ).length
      }
      const supabase = createClient()
      let query = supabase
        .from('orders')
        .select('id', { count: 'exact', head: true })
        .in('status', SIDEBAR_ACTIVE_STATUSES)
      if (roleParams.managerId) query = query.eq('manager_id', roleParams.managerId)
      if (roleParams.inspectorId) query = query.eq('inspector_id', roleParams.inspectorId)
      if (roleParams.companyId)
        query = query.or(
          `customer_company_id.eq.${roleParams.companyId},contractor_company_id.eq.${roleParams.companyId}`,
        )
      const { count, error } = await query
      if (error) throw new Error(error.message)
      return count ?? 0
    },
    staleTime: 30_000,
  })
  return activeCount
}

export function Sidebar({
  mobileOpen,
  onCloseMobile,
}: {
  mobileOpen: boolean
  onCloseMobile: () => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const profile = useAuthStore((s) => s.profile) as Profile | null
  const setProfile = useAuthStore((s) => s.setProfile)
  const collapsed = useUiStore((s) => s.sidebarCollapsed)
  const setCollapsed = useUiStore((s) => s.setSidebarCollapsed)
  const isAdmin = profile?.role === 'admin'
  const staff = isStaff(profile)
  const orderCount = useActiveOrderCount()
  const { n: mapOrdersWithCoordsCount } = useOrdersForMap({ enabled: staff })
  const { data: companiesCount = 0 } = useQuery({
    queryKey: ['sidebar-count', 'companies'],
    queryFn: async () => {
      const { total } = await listCompanies({ page: 1, pageSize: 1 })
      return total
    },
    enabled: isAdmin,
    staleTime: 30_000,
  })
  const { data: usersCount = 0 } = useQuery({
    queryKey: ['sidebar-count', 'users'],
    queryFn: async () => {
      const { total } = await listUsers({ page: 1, pageSize: 1 })
      return total
    },
    enabled: isAdmin,
    staleTime: 30_000,
  })
  // Бабл «открытых» обращений. Опрос раз в минуту (без realtime — багрепорты
  // не приходят пачками, лаг до минуты норм).
  const { data: openFeedbackCount = 0 } = useQuery({
    queryKey: ['sidebar-count', 'user-feedback-open'],
    queryFn: getOpenFeedbackCount,
    enabled: isAdmin,
    staleTime: 60_000,
    refetchInterval: 60_000,
  })
  // Бабл «новых» Avito-лидов. Та же логика, что и у user_feedback.
  const { data: newLeadsCount = 0 } = useQuery({
    queryKey: ['sidebar-count', 'inspection-leads-new'],
    queryFn: getNewLeadsCount,
    enabled: staff,
    staleTime: 60_000,
    refetchInterval: 60_000,
  })

  const groupDivider = (
    <div
      className="my-1 h-px bg-[color:var(--stryzh-color-border-subtle)]"
      aria-hidden
    />
  )

  const nav = (
    <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-2">
      {/* Оперативка */}
      <NavLink
        to="/"
        label={t('nav.dashboard')}
        count={orderCount}
        icon={LayoutDashboard}
        onNavigate={onCloseMobile}
      />
      <NavLink
        to="/orders"
        label={t('nav.orders')}
        count={orderCount}
        icon={ClipboardList}
        onNavigate={onCloseMobile}
      />
      {staff ? (
        <>
          <NavLink
            to="/map"
            label={t('nav.map')}
            count={mapOrdersWithCoordsCount}
            icon={MapIcon}
            onNavigate={onCloseMobile}
          />
          <NavLink
            to="/dispatch-map"
            label={t('nav.dispatchMap')}
            icon={Radar}
            onNavigate={onCloseMobile}
          />
          <NavLink
            to="/inspectors-positions"
            label={t('nav.inspectorsPositions')}
            icon={MapPin}
            onNavigate={onCloseMobile}
          />
          <NavLink
            to="/inspection-leads"
            label={t('leads.title')}
            count={newLeadsCount > 0 ? newLeadsCount : undefined}
            icon={Inbox}
            onNavigate={onCloseMobile}
          />
        </>
      ) : null}
      {isAdmin ? (
        <>
          {/* Справочники */}
          {groupDivider}
          <NavLink
            to="/companies"
            label={t('nav.companies')}
            count={companiesCount}
            icon={Building2}
            onNavigate={onCloseMobile}
          />
          <NavLink
            to="/users"
            label={t('nav.users')}
            count={usersCount}
            icon={Users}
            onNavigate={onCloseMobile}
          />
          <NavLink
            to="/tariffs"
            label={t('nav.tariffs')}
            icon={Tags}
            onNavigate={onCloseMobile}
          />
          {/* Финансы */}
          {groupDivider}
          <NavLink
            to="/billing"
            label={t('nav.billing')}
            icon={Wallet}
            onNavigate={onCloseMobile}
          />
          <NavLink
            to="/dkp-generator"
            label={t('nav.dkpGenerator')}
            icon={FileText}
            onNavigate={onCloseMobile}
          />
          {/* Коммуникации */}
          {groupDivider}
          <NavGroup label={t('nav.notifications')} paths={NOTIFICATIONS_PATHS} icon={Bell}>
            <NavLink
              to="/notifications/log"
              label={t('nav.notificationsLog')}
              icon={ScrollText}
              onNavigate={onCloseMobile}
            />
            <NavLink
              to="/notifications/triggers"
              label={t('nav.notificationsTriggers')}
              icon={MessageSquareText}
              onNavigate={onCloseMobile}
            />
            <NavLink
              to="/notifications/broadcast"
              label={t('nav.notificationsBroadcast')}
              icon={Megaphone}
              onNavigate={onCloseMobile}
            />
            <NavLink
              to="/notifications/subscriptions"
              label={t('nav.notificationsSubscriptions')}
              icon={BellRing}
              onNavigate={onCloseMobile}
            />
            <NavLink
              to="/notifications/app-versions"
              label={t('nav.notificationsAppVersions')}
              icon={Smartphone}
              onNavigate={onCloseMobile}
            />
          </NavGroup>
          {/* Система */}
          {groupDivider}
          <NavGroup label={t('nav.systemSettings')} paths={SYSTEM_SETTINGS_PATHS} icon={Settings}>
            <NavLink
              to="/system-config"
              label={t('nav.systemConfig')}
              icon={Settings}
              onNavigate={onCloseMobile}
            />
            <NavLink
              to="/geo-settings"
              label={t('nav.geoSettings')}
              icon={MapPin}
              onNavigate={onCloseMobile}
            />
            <NavLink
              to="/telegram-settings"
              label={t('nav.telegramLeads')}
              icon={Send}
              onNavigate={onCloseMobile}
            />
            <NavLink
              to="/sms-settings"
              label={t('nav.smsSettings')}
              icon={MessageSquareText}
              onNavigate={onCloseMobile}
            />
            <NavLink
              to="/sber-sms-settings"
              label={t('nav.sberSmsSettings')}
              icon={MessageSquareText}
              onNavigate={onCloseMobile}
            />
          </NavGroup>
          <NavLink
            to="/user-feedback"
            label={t('nav.userFeedback')}
            count={openFeedbackCount}
            icon={Bug}
            onNavigate={onCloseMobile}
          />
          {/* Логи */}
          <NavGroup label={t('nav.logs')} paths={LOGS_PATHS} icon={ScrollText}>
            <NavLink
              to="/avito-api-log"
              label={t('nav.avitoApiLog')}
              icon={Activity}
              onNavigate={onCloseMobile}
            />
            <NavLink
              to="/sber-notify-log"
              label={t('nav.sberNotifyLog')}
              icon={MessageSquareText}
              onNavigate={onCloseMobile}
            />
            <NavLink
              to="/audit"
              label={t('nav.audit')}
              icon={History}
              onNavigate={onCloseMobile}
            />
            <NavLink
              to="/client-errors"
              label={t('nav.clientErrors')}
              icon={AlertTriangle}
              onNavigate={onCloseMobile}
            />
            {profile?.has_pd_access ? (
              <NavLink
                to="/pd-log"
                label={t('nav.pdLog')}
                icon={ShieldCheck}
                onNavigate={onCloseMobile}
              />
            ) : null}
          </NavGroup>
        </>
      ) : null}
      {/* Помощь — для всех ролей */}
      {groupDivider}
      <NavLink
        to="/help"
        label={t('nav.help')}
        icon={HelpCircle}
        onNavigate={onCloseMobile}
        exact
      />
      <NavLink
        to="/help/avito"
        label={t('nav.avitoHelp')}
        icon={ScrollText}
        onNavigate={onCloseMobile}
      />
    </nav>
  )

  const widthClass = collapsed
    ? 'w-[var(--stryzh-layout-sidebar-collapsed-width)]'
    : 'w-[var(--stryzh-layout-sidebar-width)]'

  const roleLabel = roleDisplayName(profile)

  return (
    <>
      {mobileOpen ? (
        <button
          type="button"
          aria-label={t('nav.closeMenu')}
          className="fixed inset-0 z-40 bg-[color:var(--stryzh-overlay-scrim-drawer)] lg:hidden"
          onClick={onCloseMobile}
        />
      ) : null}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-50 flex min-h-dvh flex-col border-r border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] transition-[width] lg:sticky lg:top-0 lg:z-0 lg:h-dvh lg:max-h-dvh lg:shrink-0 lg:overflow-hidden lg:self-start',
          widthClass,
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        <div className="relative flex h-[var(--stryzh-layout-header-height)] shrink-0 items-center border-b border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-void)] px-3">
          {!collapsed ? (
            <Link
              to="/"
              className="absolute left-1/2 top-1/2 z-0 flex -translate-x-1/2 -translate-y-1/2 items-center"
              onClick={onCloseMobile}
            >
              <img src="/carlink.png" alt="Carlink" className="h-[24px] w-auto" />
            </Link>
          ) : (
            <span className="absolute left-1/2 top-1/2 z-0 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center">
              <img src="/carlink.png" alt="Carlink" className="h-[14px] w-auto max-w-[44px] object-contain" />
            </span>
          )}
          <button
            type="button"
            className="relative z-10 ml-auto hidden rounded-[var(--stryzh-radius-sm)] p-1 text-[color:var(--stryzh-color-text-secondary)] hover:bg-[color:var(--stryzh-color-border-subtle)] lg:inline"
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? '»' : '«'}
          </button>
        </div>
        {nav}
        <div className="mt-auto border-t border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-void)] p-3">
          {profile ? (
            <div className="mb-3 space-y-1 font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-primary)]">
              <div className="font-medium">{shortDisplayName(profile.full_name)}</div>
              <div className="text-[color:var(--stryzh-color-text-secondary)]">
                {formatPhoneDisplay(profile.phone)}
              </div>
              <div className="text-[color:var(--stryzh-color-text-secondary)]">{roleLabel}</div>
              <div className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
                Стриж-Адм v{__APP_VERSION__}
              </div>
            </div>
          ) : null}
          <div className="mb-3 flex justify-center">
            <LanguageSwitcher />
          </div>
          <Button
            variant="secondary"
            className="w-full justify-center"
            onClick={() => {
              setProfile(null)
              void signOut()
              navigate('/login')
            }}
          >
            {t('nav.logout')}
          </Button>
        </div>
      </aside>
    </>
  )
}
